// ForYouPlaces - Personalized Recommendations based on favorites

import React, { useState, useEffect } from 'react';
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Place } from '../../types';
import { getPlacesByIds, getRecommendedPlaces } from '../../services/placesService';
import { PlaceCard } from '../places/PlaceCard';

interface ForYouPlacesProps {
  favorites: string[];
  onSelectPlace: (place: Place) => void;
  onToggleFavorite: (placeId: string) => void;
}

export function ForYouPlaces({
  favorites,
  onSelectPlace,
  onToggleFavorite,
}: ForYouPlacesProps) {
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollIndex, setScrollIndex] = useState(0);

  useEffect(() => {
    async function loadRecommendations() {
      setIsLoading(true);
      try {
        // Get favorite places to analyze preferences
        const favoritePlaces = await getPlacesByIds(favorites);

        // Get recommendations based on favorites
        const recommended = await getRecommendedPlaces(
          favoritePlaces,
          favorites, // Exclude already favorited places
          8
        );

        setPlaces(recommended);
      } catch (error) {
        console.error('Error loading recommendations:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadRecommendations();
  }, [favorites]);

  const canScrollLeft = scrollIndex > 0;
  const canScrollRight = scrollIndex < Math.max(0, places.length - 4);

  const scrollLeft = () => {
    setScrollIndex((prev) => Math.max(0, prev - 1));
  };

  const scrollRight = () => {
    setScrollIndex((prev) => Math.min(places.length - 4, prev + 1));
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-[20/19] bg-gray-200 rounded-xl mb-3" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (places.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-xl">
        <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">
          {favorites.length === 0
            ? 'Speichere Orte als Favoriten, um personalisierte Empfehlungen zu erhalten!'
            : 'Keine weiteren Empfehlungen verfuegbar.'}
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Navigation Buttons */}
      {canScrollLeft && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-1/3 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={scrollRight}
          className="absolute right-0 top-1/3 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Places Grid */}
      <div className="overflow-hidden">
        <div
          className="flex gap-4 transition-transform duration-300"
          style={{ transform: `translateX(-${scrollIndex * 25}%)` }}
        >
          {places.map((place) => (
            <div key={place.id} className="w-full sm:w-1/2 md:w-1/4 flex-shrink-0">
              <PlaceCard
                place={place}
                isFavorite={favorites.includes(place.id)}
                onSelect={onSelectPlace}
                onToggleFavorite={onToggleFavorite}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Hint */}
      {favorites.length > 0 && (
        <p className="text-xs text-gray-400 mt-4 text-center">
          Basierend auf deinen {favorites.length} Favoriten
        </p>
      )}
    </div>
  );
}

export default ForYouPlaces;
